import { inject, Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, map, timeout } from 'rxjs/operators';

import { AddressSearchResult } from '../../../../api/models/address-search-result';
import { AddressesService } from '../../../../api/services/addresses.service';
import type { Entry } from '../../../../features/admin/modules/modules.model';
import { debugLog } from '../../../utils/debug-logger';
import { DynamicOptionsService, LinkedFieldData } from './dynamic-options.service';
import { AddressbookOption, AddressbookOptionSourceEnum } from './addressbook-search.types';

/** Beyond this the external provider is treated as unavailable — see `searchMaps`. */
const MAPS_TIMEOUT_MS = 1500;

/**
 * Shortest query worth sending to the external provider. One or two characters match half the
 * country; the response is noise and it costs a third-party call on every debounced keystroke,
 * on every one of these fields on the screen. The addressbook half is unaffected — it is a local
 * module query and its results are useful from the first character.
 */
const MIN_EXTERNAL_QUERY_LENGTH = 3;

/**
 * The two-source search behind the addressbook autocomplete field: saved addressbook entries
 * first, external Maps suggestions after them.
 *
 * Lives in a service rather than in the field component so a second two-source field can reuse it,
 * mirroring the split `DynamicOptionsService` already makes for the module-entry source.
 *
 * ## Why the two sources page differently
 *
 * The addressbook source is a normal module query and pages properly (`offset`/`limit`). The Maps
 * source does NOT: `POST /addresses/search` takes an `AddressSearch` body of search fields only —
 * no offset, no limit, no cursor (src/app/api/models/address-search.ts) — and answers with a flat
 * array. Asking again for the same query returns the same batch, so there is no second Maps page
 * to load. `searchBook()` therefore exists as a separate entry point and a Maps equivalent
 * deliberately does not: the user scrolls through the Maps batch that arrived with the first
 * response, and only the addressbook keeps loading. Give `/addresses/search` a cursor and this is
 * where the second call belongs.
 */
@Injectable({ providedIn: 'root' })
export class AddressbookSearchService {
  readonly #addressesService = inject(AddressesService);
  readonly #dynamicOptionsService = inject(DynamicOptionsService);

  /**
   * Both sources for one query. The addressbook page comes back first in the list, Maps after it,
   * with Maps hits that duplicate an addressbook row already dropped.
   *
   * Neither source may take the other down: a failing Maps lookup still leaves the user their own
   * saved addresses, and an empty addressbook still leaves them the Maps suggestions. The merge
   * itself is guarded too — `forkJoin` completing is not the same as the merge succeeding, and an
   * uncaught throw here would leave the field's spinner up and its scroll handler wedged.
   */
  search(
    filterValue: unknown,
    linkedData: LinkedFieldData[],
    searchTerm: string,
    field: FormlyFieldConfig,
    offset: number,
    limit: number
  ): Observable<AddressbookOption[]> {
    // Both legs always emit (see `defaultIfEmpty` in `searchBook` and `searchMaps`). That matters
    // here because `forkJoin` emits only once EVERY source has emitted: one source that completes
    // silently would hide the other one entirely and leave the field spinning.
    return forkJoin({
      book: this.searchBook(filterValue, linkedData, searchTerm, field, offset, limit),
      maps: this.searchMaps(searchTerm),
    }).pipe(
      map(({ book, maps }) => [...book, ...this.withoutDuplicates(maps, book)]),
      catchError(error => {
        debugLog('AddressbookSearch', 'merging the two sources failed', error);
        console.error('Addressbook search failed', error);
        return of([] as AddressbookOption[]);
      })
    );
  }

  /**
   * One page of saved addressbook entries, as options the autocomplete template can render.
   *
   * Always emits, also when the module source completes without emitting — which is what
   * `forkJoin([])` does for an empty `linkedData` (see the note on
   * `DynamicOptionsService.fetchOptionsForValue` for when that happens). That method guards it itself;
   * the guard is repeated here because this method is public and called on its own by
   * scroll-to-load, and a caller that never hears back keeps its spinner up for good.
   */
  searchBook(
    filterValue: unknown,
    linkedData: LinkedFieldData[],
    searchTerm: string,
    field: FormlyFieldConfig,
    offset: number,
    limit: number
  ): Observable<AddressbookOption[]> {
    return this.#dynamicOptionsService
      .fetchOptionsForValue(filterValue, linkedData, searchTerm, field, offset, limit)
      .pipe(
        map(options => (options ?? []).map(option => this.#asBookOption(option))),
        defaultIfEmpty([] as AddressbookOption[]),
        catchError(error => {
          // Swallowed so the other source still renders, but never silently: without a log a 500
          // on the addressbook is indistinguishable from an empty addressbook, for the user AND
          // for whoever reads the console afterwards.
          debugLog('AddressbookSearch', 'addressbook lookup failed', error);
          console.error('Addressbook module lookup failed', error);
          return of([] as AddressbookOption[]);
        })
      );
  }

  /**
   * The external lookup.
   *
   * Short queries are not sent at all (see MIN_EXTERNAL_QUERY_LENGTH). That also covers the empty
   * one, which matters because the panel opens with an empty query every time.
   *
   * Bounded by a timeout because this is the one leg of `search()` that goes to a third party. The
   * two sources are combined with `forkJoin`, so a provider that hangs holds back the user's OWN
   * saved addresses for as long as the ceiling allows — the worst possible trade, since the
   * addressbook half is local and already answered. 1.5s is past "slow" and short of "broken".
   * ponytail: a fixed ceiling, not progressive rendering. Paint the two halves separately (emit
   * book first, merged second) if the external provider turns out to be routinely slow rather than
   * occasionally dead.
   */
  searchMaps(searchTerm: string): Observable<AddressbookOption[]> {
    const query = searchTerm?.trim() ?? '';
    if (query.length < MIN_EXTERNAL_QUERY_LENGTH) {
      return of([]);
    }

    return this.#addressesService.searchAddress({ body: { query } }).pipe(
      timeout(MAPS_TIMEOUT_MS),
      map(results => this.#withoutRepeatedLabels((results ?? []).map(result => this.#asMapsOption(result)))),
      defaultIfEmpty([] as AddressbookOption[]),
      catchError(error => {
        debugLog('AddressbookSearch', 'external address lookup failed', error);
        console.error('External address lookup failed', error);
        return of([] as AddressbookOption[]);
      })
    );
  }

  /**
   * Turns a Maps hit into the addressbook `Entry` shape this field always stores.
   *
   * No `id`: nothing has been saved yet. That absence is the signal the field reads to offer
   * "Adres toevoegen aan adresboek" and to keep the value valid without looking it up in the
   * module (see the component's `validateOptionExists` override).
   *
   * The sub-keys match the Adressenboek module's composite `address` field exactly, so a later
   * save through the create-form lands in the fields an admin configured, not next to them.
   * Coordinates are stringified for the same reason: `address.component.ts` stores them as strings,
   * and a number here would make the same address compare unequal depending on where it came from.
   */
  toAddressbookEntry(result: AddressSearchResult): Entry {
    const label = this.formatLabel(result);
    return {
      entry: {
        archived: false,
        name: label,
        address: {
          manual: false,
          fullAddress: result.fullAddress ?? label,
          streetName: result.streetName ?? '',
          number: result.number ?? '',
          numberSuffix: result.numberSuffix ?? '',
          postalCode: result.postalCode ?? '',
          city: result.city ?? '',
          country: result.country ?? '',
          latitude: String(result.latitude ?? ''),
          longitude: String(result.longitude ?? ''),
        },
      },
      selectedInput: label,
    };
  }

  /** The one-line address, in the same order the composite address field composes it. */
  formatLabel(result: AddressSearchResult): string {
    const houseNumber = result.number != null ? `${result.number}${result.numberSuffix ?? ''}` : '';
    const street = [result.streetName, houseNumber].filter(Boolean).join(' ');
    return [street, result.postalCode, result.city, result.country].filter(Boolean).join(', ');
  }

  /**
   * Drops Maps hits the user already has saved. Matched on `addressIdentity` (postcode + house
   * number, plus street outside the Dutch format), not on the composed label: the same address reaches us as "Kerkstraat 1, 3581RA, Utrecht" from
   * the addressbook and "Kerkstraat 1, 3581 RA, Utrecht, Nederland" from Maps, so comparing labels
   * would show both rows and let the user save a duplicate of what they already own.
   *
   * Public because scroll-to-load needs it too: a later addressbook page can contain the saved
   * twin of a Maps row that is already on screen, and only the caller knows which rows those are.
   */
  withoutDuplicates(maps: AddressbookOption[], book: AddressbookOption[]): AddressbookOption[] {
    const seen = new Set(book.map(option => addressIdentity(option.value)).filter(Boolean));
    return maps.filter(option => {
      const identity = addressIdentity(option.value);
      return !identity || !seen.has(identity);
    });
  }

  #asBookOption(option: { label?: string; value?: Entry; disabled?: boolean }): AddressbookOption {
    return {
      label: option.label ?? '',
      value: option.value as Entry,
      source: AddressbookOptionSourceEnum.Book,
      disabled: option.disabled,
    };
  }

  #asMapsOption(result: AddressSearchResult): AddressbookOption {
    return {
      label: this.formatLabel(result),
      value: this.toAddressbookEntry(result),
      source: AddressbookOptionSourceEnum.Maps,
    };
  }

  /**
   * Collapses Maps hits that compose to the same line.
   *
   * A geocoder happily returns a street twice with different coordinates when the query has no
   * house number; both render as the identical row. Two reasons to drop the repeat: the list is
   * keyed on `value.id || label` and Maps rows have no id, so duplicates are a hard NG0955 — and
   * picking a row resolves it BY label, so a user clicking the second one would silently get the
   * first one's coordinates.
   */
  #withoutRepeatedLabels(options: AddressbookOption[]): AddressbookOption[] {
    const seen = new Set<string>();
    return options.filter(option => {
      if (seen.has(option.label)) {
        return false;
      }
      seen.add(option.label);
      return true;
    });
  }
}

/** Dutch postcode after normalising: four digits, two letters. No other European format looks like it. */
const DUTCH_POSTCODE = /^\d{4}[a-z]{2}$/;

/**
 * Postcode + house number, normalised: no spaces, no case. Empty when the entry carries neither,
 * which the caller reads as "cannot be compared" rather than "matches everything".
 *
 * A Dutch postcode narrows to a street or two, so postcode + number names one building. Elsewhere it
 * does not: Belgian "1000" is all of Brussels-city, German and French postcodes span districts or
 * whole municipalities, and "1000|1" would match every house number 1 in them. Outside the Dutch
 * format street and city join the key — city because one Belgian or rural German postcode covers
 * several villages, each with its own "Kerkstraat 1". When Maps and the addressbook spell either
 * differently the keys miss and both rows show — a duplicate row is the safe failure, a vanished
 * address is not.
 *
 * The country stays out on purpose: it arrives as "Nederland", "Netherlands", "NL" or empty
 * depending on where the entry came from, and would split matches that are correct today.
 */
export function addressIdentity(entry: Entry | null | undefined): string {
  const address = entry?.entry?.['address'] as Record<string, unknown> | undefined;
  if (!address) {
    return '';
  }

  const postalCode = String(address['postalCode'] ?? '')
    .replace(/\s+/g, '')
    .toLowerCase();
  const number = String(address['number'] ?? '').trim();
  const suffix = String(address['numberSuffix'] ?? '')
    .replace(/\s+/g, '')
    .toLowerCase();

  if (!postalCode || !number) {
    return '';
  }
  if (DUTCH_POSTCODE.test(postalCode)) {
    return `${postalCode}|${number}${suffix}`;
  }

  const street = normalisedName(address['streetName']);
  return street ? `${postalCode}|${normalisedName(address['city'])}|${street}|${number}${suffix}` : '';
}

/** Case, accents, spaces and punctuation out: "Rue Neuve", "rue-neuve" and "Rüe Neuve" agree. */
function normalisedName(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[^\p{L}\p{N}]/gu, '')
    .toLowerCase();
}
